/**
 * Dietary comparison utilities for frontend.
 * 
 * Handles comparison of ingredient dietary attributes with user preferences
 * and health concerns to determine matches and conflicts.
 */

// Type definitions
export type LimitType = "max" | "min" | "beneficial_any" | "neutral";
export type NutrientUnit = "g" | "kcal" | "index";

export interface NutrientLimit {
  type: LimitType;
  value?: number;
  unit: NutrientUnit;
}

export interface HealthConcernLimits {
  [nutrient: string]: NutrientLimit;
}

export interface AllHealthConcernLimits {
  [concern: string]: HealthConcernLimits;
}

export interface NutritionalProfile {
  confirmed?: boolean;
  protein_per_100g?: string;
  calories_per_100g?: string;
  carbohydrates_per_100g?: string;
  fiber_per_100g?: string;
  sugar_per_100g?: string;
  saturated_fat_per_100g?: string;
  trans_fat_per_100g?: string;
  omega3_per_100g?: string;
  sodium_per_100g?: string;
  potassium_per_100g?: string;
  iron_per_100g?: string;
  calcium_per_100g?: string;
  magnesium_per_100g?: string;
  phosphorus_per_100g?: string;
  cholesterol_per_100g?: string;
}

export interface UserHealthConcern {
  name: string;
  type: 'min' | 'max';
  value: number;
}

// Health concern nutritional limits (replicated from backend)
export const HEALTH_CONCERN_LIMITS: AllHealthConcernLimits = {
  "high_blood_pressure": {
    "sodium_per_100g": { type: "max", value: 0.15, unit: "g" },
    "potassium_per_100g": { type: "min", value: 0.3, unit: "g" },
    "protein_per_100g": { type: "neutral", unit: "g" },
    "calories_per_100g": { type: "neutral", unit: "kcal" },
    "sugar_per_100g": { type: "max", value: 5.0, unit: "g" },
    "cholesterol_per_100g": { type: "max", value: 0.01, unit: "g" },
    "saturated_fat_per_100g": { type: "max", value: 1.5, unit: "g" },
    "trans_fat_per_100g": { type: "max", value: 0.0, unit: "g" },
    "fiber_per_100g": { type: "min", value: 3.0, unit: "g" },
    "iron_per_100g": { type: "neutral", unit: "g" },
    "calcium_per_100g": { type: "min", value: 0.1, unit: "g" },
    "magnesium_per_100g": { type: "min", value: 0.05, unit: "g" },
    "phosphorus_per_100g": { type: "neutral", unit: "g" },
    "omega3_per_100g": { type: "beneficial_any", unit: "g" },
    "gluten_per_100g": { type: "neutral", unit: "g" },
    "lactose_per_100g": { type: "neutral", unit: "g" },
    "folate_per_100g": { type: "neutral", unit: "g" },
    "vitamin_d_per_100g": { type: "neutral", unit: "g" },
    "carbohydrates_per_100g": { type: "neutral", unit: "g" },
  },
  "high_cholesterol": {
    "saturated_fat_per_100g": { type: "max", value: 1.5, unit: "g" },
    "trans_fat_per_100g": { type: "max", value: 0.0, unit: "g" },
    "cholesterol_per_100g": { type: "max", value: 0.01, unit: "g" },
    "fiber_per_100g": { type: "min", value: 3.0, unit: "g" },
    "omega3_per_100g": { type: "min", value: 0.2, unit: "g" },
    "potassium_per_100g": { type: "beneficial_any", unit: "g" },
    "calories_per_100g": { type: "neutral", unit: "kcal" },
    "sugar_per_100g": { type: "neutral", unit: "g" },
    "sodium_per_100g": { type: "neutral", unit: "g" },
    "protein_per_100g": { type: "neutral", unit: "g" },
    "iron_per_100g": { type: "neutral", unit: "g" },
    "calcium_per_100g": { type: "neutral", unit: "g" },
    "magnesium_per_100g": { type: "neutral", unit: "g" },
    "phosphorus_per_100g": { type: "neutral", unit: "g" },
    "gluten_per_100g": { type: "neutral", unit: "g" },
    "lactose_per_100g": { type: "neutral", unit: "g" },
    "folate_per_100g": { type: "neutral", unit: "g" },
    "vitamin_d_per_100g": { type: "neutral", unit: "g" },
    "carbohydrates_per_100g": { type: "neutral", unit: "g" },
  },
  "diabetes": {
    "sugar_per_100g": { type: "max", value: 5.0, unit: "g" },
    "carbohydrates_per_100g": { type: "max", value: 20.0, unit: "g" },
    "fiber_per_100g": { type: "min", value: 3.0, unit: "g" },
    "saturated_fat_per_100g": { type: "max", value: 1.5, unit: "g" },
    "trans_fat_per_100g": { type: "max", value: 0.0, unit: "g" },
    "protein_per_100g": { type: "neutral", unit: "g" },
    "calories_per_100g": { type: "neutral", unit: "kcal" },
    "cholesterol_per_100g": { type: "neutral", unit: "g" },
    "sodium_per_100g": { type: "neutral", unit: "g" },
    "potassium_per_100g": { type: "neutral", unit: "g" },
    "iron_per_100g": { type: "neutral", unit: "g" },
    "calcium_per_100g": { type: "neutral", unit: "g" },
    "magnesium_per_100g": { type: "neutral", unit: "g" },
    "phosphorus_per_100g": { type: "neutral", unit: "g" },
    "omega3_per_100g": { type: "neutral", unit: "g" },
    "gluten_per_100g": { type: "neutral", unit: "g" },
    "lactose_per_100g": { type: "neutral", unit: "g" },
    "folate_per_100g": { type: "neutral", unit: "g" },
    "vitamin_d_per_100g": { type: "neutral", unit: "g" },
  },
  "celiac_disease": {
    "gluten_per_100g": { type: "max", value: 0.0, unit: "g" },
    "calories_per_100g": { type: "neutral", unit: "kcal" },
    "sugar_per_100g": { type: "neutral", unit: "g" },
    "cholesterol_per_100g": { type: "neutral", unit: "g" },
    "saturated_fat_per_100g": { type: "neutral", unit: "g" },
    "trans_fat_per_100g": { type: "neutral", unit: "g" },
    "fiber_per_100g": { type: "neutral", unit: "g" },
    "protein_per_100g": { type: "neutral", unit: "g" },
    "sodium_per_100g": { type: "neutral", unit: "g" },
    "potassium_per_100g": { type: "neutral", unit: "g" },
    "iron_per_100g": { type: "neutral", unit: "g" },
    "calcium_per_100g": { type: "neutral", unit: "g" },
    "magnesium_per_100g": { type: "neutral", unit: "g" },
    "phosphorus_per_100g": { type: "neutral", unit: "g" },
    "omega3_per_100g": { type: "neutral", unit: "g" },
    "lactose_per_100g": { type: "neutral", unit: "g" },
    "folate_per_100g": { type: "neutral", unit: "g" },
    "vitamin_d_per_100g": { type: "neutral", unit: "g" },
    "carbohydrates_per_100g": { type: "neutral", unit: "g" },
  },
  "lactose_intolerance": {
    "lactose_per_100g": { type: "max", value: 0.0, unit: "g" },
    "calories_per_100g": { type: "neutral", unit: "kcal" },
    "sugar_per_100g": { type: "neutral", unit: "g" },
    "cholesterol_per_100g": { type: "neutral", unit: "g" },
    "saturated_fat_per_100g": { type: "neutral", unit: "g" },
    "trans_fat_per_100g": { type: "neutral", unit: "g" },
    "fiber_per_100g": { type: "neutral", unit: "g" },
    "protein_per_100g": { type: "neutral", unit: "g" },
    "sodium_per_100g": { type: "neutral", unit: "g" },
    "potassium_per_100g": { type: "neutral", unit: "g" },
    "iron_per_100g": { type: "neutral", unit: "g" },
    "calcium_per_100g": { type: "neutral", unit: "g" },
    "magnesium_per_100g": { type: "neutral", unit: "g" },
    "phosphorus_per_100g": { type: "neutral", unit: "g" },
    "omega3_per_100g": { type: "neutral", unit: "g" },
    "gluten_per_100g": { type: "neutral", unit: "g" },
    "folate_per_100g": { type: "neutral", unit: "g" },
    "vitamin_d_per_100g": { type: "neutral", unit: "g" },
    "carbohydrates_per_100g": { type: "neutral", unit: "g" },
  },
  "kidney_disease": {
    "protein_per_100g": { type: "max", value: 5.0, unit: "g" },
    "sodium_per_100g": { type: "max", value: 0.15, unit: "g" },
    "potassium_per_100g": { type: "max", value: 0.25, unit: "g" },
    "phosphorus_per_100g": { type: "max", value: 0.05, unit: "g" },
    "calories_per_100g": { type: "neutral", unit: "kcal" },
    "sugar_per_100g": { type: "neutral", unit: "g" },
    "cholesterol_per_100g": { type: "neutral", unit: "g" },
    "saturated_fat_per_100g": { type: "neutral", unit: "g" },
    "trans_fat_per_100g": { type: "neutral", unit: "g" },
    "fiber_per_100g": { type: "neutral", unit: "g" },
    "iron_per_100g": { type: "neutral", unit: "g" },
    "calcium_per_100g": { type: "neutral", unit: "g" },
    "magnesium_per_100g": { type: "neutral", unit: "g" },
    "omega3_per_100g": { type: "neutral", unit: "g" },
    "gluten_per_100g": { type: "neutral", unit: "g" },
    "lactose_per_100g": { type: "neutral", unit: "g" },
    "folate_per_100g": { type: "neutral", unit: "g" },
    "vitamin_d_per_100g": { type: "neutral", unit: "g" },
    "carbohydrates_per_100g": { type: "neutral", unit: "g" },
  },
};

/**
 * Get matching dietary attributes between ingredient and user preferences
 */
export function getMatchingDietaryAttributes(
  ingredientDietaryInfo: any,
  userPreferences: any
): string[] {
  if (!ingredientDietaryInfo || !userPreferences) return [];
  
  const matches: string[] = [];
  const dietaryFields = [
    'vegan', 'vegetarian', 'pescatarian', 'gluten_free', 'dairy_free',
    'nut_free', 'egg_free', 'soy_free', 'shellfish_free', 'fish_free',
    'sesame_free', 'low_fodmap', 'keto', 'paleo', 'atkins',
    'corn_free', 'mustard_free'
  ];
  
  for (const field of dietaryFields) {
    if (ingredientDietaryInfo[field] && userPreferences[field]) {
      matches.push(field);
    }
  }
  
  return matches;
}

/**
 * Evaluate health concern conflicts and matches based on nutritional profile
 */
export function evaluateHealthConcerns(
  nutritionalProfile: NutritionalProfile | undefined,
  userHealthConcerns: UserHealthConcern[]
): { conflicts: string[]; matches: string[] } {
  if (!nutritionalProfile || !userHealthConcerns.length) {
    return { conflicts: [], matches: [] };
  }

  const conflicts: string[] = [];
  const matches: string[] = [];

  try {
    for (const healthConcern of userHealthConcerns) {
      const concernName = healthConcern.name;
      const concernLimits = HEALTH_CONCERN_LIMITS[concernName];
      
      if (!concernLimits) {
        continue;
      }

      // Track conflicts and matches for this concern
      const maxConflicts: string[] = [];
      const concernMatches: string[] = [];

      // Check each nutrient limit
      for (const [nutrientKey, limitConfig] of Object.entries(concernLimits)) {
        if (nutrientKey === "severity" || nutrientKey === "reason") {
          continue; // Skip metadata fields
        }

        // Get the ingredient's value for this nutrient
        const ingredientValueStr = (nutritionalProfile as any)[nutrientKey];
        if (!ingredientValueStr) {
          continue;
        }

        // Convert string to float for comparison
        const ingredientValue = parseFloat(ingredientValueStr);
        if (isNaN(ingredientValue)) {
          continue;
        }

        const limitType = limitConfig.type;
        const limitValue = limitConfig.value;

        if (limitType === "max") {
          if (limitValue !== undefined && ingredientValue > limitValue) {
            // Include health concern name in the conflict
            const nutrientDisplay = nutrientKey.replace('_per_100g', '').replace('_', ' ').toUpperCase();
            const unit = limitConfig.unit;
            maxConflicts.push(`${concernName}|${nutrientDisplay}: ${ingredientValue.toFixed(1)}${unit}`);
          }
        } else if (limitType === "min") {
          if (limitValue !== undefined && ingredientValue >= limitValue) {
            // Include the actual value in the match
            const unit = limitConfig.unit;
            concernMatches.push(`${concernName}: ${ingredientValue.toFixed(1)}${unit}`);
          }
        } else if (limitType === "beneficial_any") {
          if (ingredientValue > 0) {
            concernMatches.push(concernName);
          }
        } else if (limitType === "neutral") {
          // Neutral nutrients don't affect the evaluation
        }
      }

      // Determine final result for this health concern
      if (maxConflicts.length > 0) {
        // Any max conflicts = CONFLICT
        conflicts.push(...maxConflicts);
      } else if (concernMatches.length > 0) {
        // No conflicts but has matches = MATCH
        matches.push(...concernMatches);
      }
    }

    return { conflicts, matches };
  } catch (error) {
    console.error('Error evaluating health concerns:', error);
    return { conflicts: [], matches: [] };
  }
}

/**
 * Get all relevant tags for an ingredient (dietary + health concerns)
 */
export function getAllRelevantTags(
  ingredientDietaryInfo: any,
  userPreferences: any,
  nutritionalProfile: NutritionalProfile | undefined,
  userHealthConcerns: UserHealthConcern[]
): { dietaryMatches: string[]; healthConflicts: string[]; healthMatches: string[] } {
  const dietaryMatches = getMatchingDietaryAttributes(ingredientDietaryInfo, userPreferences);
  const { conflicts: healthConflicts, matches: healthMatches } = evaluateHealthConcerns(
    nutritionalProfile,
    userHealthConcerns
  );

  return {
    dietaryMatches,
    healthConflicts,
    healthMatches
  };
}
